"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  findHedgesAndBoosters,
  type StackedSentence,
  type StanceKind,
  type StanceReport,
} from "@/lib/tool-kit/writing-analysis/hedges-boosters";

/** English strings. A translated content file overrides these through `ui`. */
export const HEDGING_LANGUAGE_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a discussion section, an abstract or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "These findings suggest that mindfulness training may reduce exam anxiety, although the effect was relatively small. Students who practised daily tended to report lower stress, which indicates that the frequency of practice could matter. However, the results clearly demonstrate that the intervention works for every student, and they prove that schools must adopt it. Because the sample was drawn from one university, the findings are probably not representative, and the long-term benefits remain unclear. It is possible that motivated students were simply more likely to volunteer.",
  enterPrompt:
    "Paste or type some text to see its hedges (may, suggest, possibly) and boosters (clearly, prove, always).",
  results: "Results",
  words: "Words",
  hedges: "Hedges",
  boosters: "Boosters",
  hedgesPer1000: "Hedges per 1,000 words",
  boostersPer1000: "Boosters per 1,000 words",
  ratio: "Hedges per booster",
  noBoosters: "No boosters",
  balanceHeading: "Balance",
  balanceLabel: "Hedges {hedgeShare}, boosters {boosterShare}",
  readingBoostersLead:
    "Boosters outnumber hedges. Published research usually runs the other way, so check each strong claim against the evidence behind it.",
  readingHedgesLead:
    "Hedges outnumber boosters, as they usually do in published research. Check that no single claim is hedged twice over.",
  readingEven:
    "Hedges and boosters are level. Neither is wrong — check that each matches the strength of its evidence.",
  readingNone:
    "No hedges or boosters found. Every claim is stated flatly — make sure that matches what your evidence can support.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with hedges and boosters highlighted",
  legendHedge: "Hedge",
  legendBooster: "Booster",
  hoverHint:
    "Hover over a highlight to see what kind of hedge or booster it is.",
  kindModal:
    "Hedge — a modal of possibility. (“Could” can also mean past ability.)",
  kindVerb: "Hedge — a verb that reports or softens the claim",
  kindProbability: "Hedge — marks the claim as probable rather than certain",
  kindFrequency: "Hedge — limits the claim to most cases",
  kindApproximator: "Hedge — marks a figure or a degree as approximate",
  kindCertainty: "Booster — presents the claim as certain",
  kindAbsolute: "Booster — an absolute: check it holds without exception",
  kindProof:
    "Booster — “prove” and “demonstrate” claim more than most evidence allows",
  kindNecessity:
    "Booster when it states certainty (“this must be the cause”); not when it states a requirement",
  topHedges: "Hedges you use most",
  topBoosters: "Boosters you use most",
  colWord: "Word or phrase",
  colTimes: "Times",
  stackedHedgesHeading: "Sentences with three or more hedges",
  stackedHedgesHint:
    "One hedge is usually enough. Several in a row can make a claim sound as if you doubt it.",
  stackedBoostersHeading: "Sentences with two or more boosters",
  stackedBoostersHint:
    "Stacked boosters overclaim fastest. Make sure the evidence carries the sentence.",
  hedgeCount: "one: hedge|other: hedges",
  boosterCount: "one: booster|other: boosters",
} as const;

type Strings = typeof HEDGING_LANGUAGE_CHECKER_STRINGS;

const KIND_TITLES: Record<StanceKind, keyof Strings> = {
  modal: "kindModal",
  verb: "kindVerb",
  probability: "kindProbability",
  frequency: "kindFrequency",
  approximator: "kindApproximator",
  certainty: "kindCertainty",
  absolute: "kindAbsolute",
  proof: "kindProof",
  necessity: "kindNecessity",
};

const TONES = { hedge: "sky", booster: "rose" } as const;

function reading(report: StanceReport, strings: Strings): string {
  if (report.hedges === 0 && report.boosters === 0) {
    return strings.readingNone;
  }
  if (report.boosters > report.hedges) {
    return strings.readingBoostersLead;
  }
  return report.hedges > report.boosters
    ? strings.readingHedgesLead
    : strings.readingEven;
}

function StackedList({
  heading,
  hint,
  sentences,
  countLabel,
}: {
  heading: string;
  hint: string;
  sentences: readonly StackedSentence[];
  countLabel: (count: number) => string;
}) {
  if (sentences.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{heading}</h3>
      <p className={HINT}>{hint}</p>
      <ul className="space-y-2">
        {sentences.map((sentence) => (
          <li className={CARD} key={sentence.start}>
            <p className="text-foreground text-sm leading-relaxed">
              {sentence.text}
            </p>
            <p className="mt-2 font-medium text-muted-foreground text-xs">
              {countLabel(sentence.count)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function BalanceBar({
  report,
  strings,
  fmt,
  locale,
}: {
  report: StanceReport;
  strings: Strings;
  fmt: (value: number, options?: Intl.NumberFormatOptions) => string;
  locale: string;
}) {
  const total = report.hedges + report.boosters;
  if (total === 0) {
    return null;
  }
  const hedgeShare = report.hedges / total;
  const percent = { style: "percent", maximumFractionDigits: 0 } as const;
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.balanceHeading}</h3>
      <div
        aria-label={t(strings.balanceLabel, {
          hedgeShare: fmt(hedgeShare, percent),
          boosterShare: fmt(1 - hedgeShare, percent),
        })}
        className="flex h-3 overflow-hidden rounded-full bg-muted"
        role="img"
      >
        <div
          className="bg-sky-400/70"
          style={{ width: `${hedgeShare * 100}%` }}
        />
        <div
          className="bg-rose-400/70"
          style={{ width: `${(1 - hedgeShare) * 100}%` }}
        />
      </div>
      <p className={HINT}>
        {fmt(report.hedges)}
        {tPlural(strings.hedgeCount, locale, report.hedges)}
        {" · "}
        {fmt(report.boosters)}
        {tPlural(strings.boosterCount, locale, report.boosters)}
      </p>
    </div>
  );
}

export default function HedgingLanguageCheckerWidget() {
  const strings = useToolUi(HEDGING_LANGUAGE_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(() => findHedgesAndBoosters(deferred), [deferred]);

  const highlights: TextMatch[] = report.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: match.category,
    title: strings[KIND_TITLES[match.kind]],
  }));
  const hasText = deferred.trim().length > 0;
  const oneDecimal = { maximumFractionDigits: 1 };

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="hl-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite" className="space-y-4">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.hedges}
                value={fmt(report.hedges)}
              />
              <StatCard
                emphasis
                label={strings.boosters}
                value={fmt(report.boosters)}
              />
              <StatCard
                label={strings.ratio}
                value={
                  report.ratio === null
                    ? strings.noBoosters
                    : fmt(report.ratio, oneDecimal)
                }
              />
              <StatCard
                label={strings.hedgesPer1000}
                value={fmt(report.hedgesPer1000, oneDecimal)}
              />
              <StatCard
                label={strings.boostersPer1000}
                value={fmt(report.boostersPer1000, oneDecimal)}
              />
              <StatCard label={strings.words} value={fmt(report.words)} />
            </StatGrid>
            <Notice>{reading(report, strings)}</Notice>
          </div>

          <BalanceBar
            fmt={fmt}
            locale={locale}
            report={report}
            strings={strings}
          />

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <p className="flex flex-wrap gap-3 text-sm">
              <span className={HIGHLIGHT_TONES.sky}>{strings.legendHedge}</span>
              <span className={HIGHLIGHT_TONES.rose}>
                {strings.legendBooster}
              </span>
            </p>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={TONES}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          <div className="grid gap-6 sm:grid-cols-2">
            {report.topHedges.length > 0 ? (
              <div className="space-y-3">
                <h3 className={WIDGET_H3}>{strings.topHedges}</h3>
                <DataTable
                  head={[strings.colWord, strings.colTimes]}
                  rows={report.topHedges.map((row) => [
                    row.key,
                    fmt(row.count),
                  ])}
                />
              </div>
            ) : null}
            {report.topBoosters.length > 0 ? (
              <div className="space-y-3">
                <h3 className={WIDGET_H3}>{strings.topBoosters}</h3>
                <DataTable
                  head={[strings.colWord, strings.colTimes]}
                  rows={report.topBoosters.map((row) => [
                    row.key,
                    fmt(row.count),
                  ])}
                />
              </div>
            ) : null}
          </div>

          <StackedList
            countLabel={(count) =>
              `${fmt(count)}${tPlural(strings.hedgeCount, locale, count)}`
            }
            heading={strings.stackedHedgesHeading}
            hint={strings.stackedHedgesHint}
            sentences={report.stackedHedges}
          />
          <StackedList
            countLabel={(count) =>
              `${fmt(count)}${tPlural(strings.boosterCount, locale, count)}`
            }
            heading={strings.stackedBoostersHeading}
            hint={strings.stackedBoostersHint}
            sentences={report.stackedBoosters}
          />
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}
    </div>
  );
}
